import React, { useEffect, useState } from 'react'
import { useEmailConfirmationMutation } from '../../Redux/Register/registerSlice'
import { useTitleContext } from "../../Hooks/useTitleContext"
import { FoundationsGenericToaster } from '../../GenericComponents/FoundationsGenericToaster'
import { useSelector } from 'react-redux'
import { getAppWorkflowState, RootState } from '../../Redux/store'
import { appWorkflowStates } from '../../Types/appWorkflowStates'
import PageNotAccessibleIfLoggedIn from '../ResetPassword/PageNotAcessibleIfLoggedIn'
import { FoundationsEnums } from '../../enum'

interface EmailConfirmationData {
  Id: string;
  TimeId: string;
}

interface APIResponseData {
  EmailAddress?: string;
  MessageResults?: Array<{
    Message: string;
    HelpMessage: string;
  }>;
}

// ------------------
// Success View
// ------------------
interface EmailConfirmationSuccessProps {
  emailAddress: string;
}

const EmailConfirmationSuccess: React.FC<EmailConfirmationSuccessProps> = ({ emailAddress }) => {
  return (
    <div className="row">
      <div className="col-12 offset-sm-2 col-sm-6">
        <div className="row">
          <div className="col-12 mb-5 text-left">
            <h2 className="header">Confirm Account Registration</h2>
            <div className="status-box-success">
              Successfully confirmed your account registration.
            </div>
            <div className="padding-top-large">
              You have successfully confirmed your registration with Foundations for{' '}
              <span id="label_emailaddress_success" className="strong">
                {emailAddress}
              </span>.
            </div>
            <div className="sub-header-highlight padding-top-large">What's Next?</div>
            <div className="padding-top-small">
              Foundations administrators will{' '}
              <span className="strong">verify and approve your registration</span> and will send
              another email which will allow you to access the application.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ------------------
// Failure View
// ------------------
const EmailConfirmationFailed: React.FC = () => {
  return (
    <div className="row">
      <div className="col-8" id="register_confirmation_el">
        <div className="row text-left ml-2 ml-md-5">
          <div className="col-12 mb-5">
            <div className="header">Confirm Account Registration</div>
            <div className="status-box-error">
              Sorry, confirming your account registration failed.
            </div>
          </div>
          <div className="col-12 mb-3 ml-2 ml-md-3 pl-2">
            <h5 className="sub-header-highlight mb-2">Possible Reasons</h5>
            <ul style={{ listStyleType: 'disc' }}>
              <li style={{ listStyle: 'disc' }}>
                The confirmation link may have expired or is invalid.
              </li>
              <li style={{ listStyle: 'disc' }}>
                You may have already confirmed your registration.
              </li>
            </ul>
          </div>
          <div className="col-12 mb-3 ml-2 ml-md-3 pl-2">
            <h5 className="sub-header-highlight mb-2">What's Next?</h5>
            <ul style={{ listStyleType: 'disc' }}>
              <li style={{ listStyle: 'disc' }}>
                If you have an approved account, you can <a href="#signin">sign in here</a> to use the application.
              </li>
              <li style={{ listStyle: 'disc' }}>
                You can <a href="#send-email-confirmation">request us</a> to send another confirm registration email.
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="col-4">
        <div className="main-right-container">
          <div className="content">
            <div className="header">Confirm Account</div>
            <div>
              You can{' '}
              <a id="link_confirm_reg_1" href="#send-email-confirmation">
                request us
              </a>{' '}
              to send another confirm registration email to you.
            </div>
          </div>
          <div className="padding-top-largest" />
          <div className="content">
            <div className="header">Need Help</div>
            <div>
              Trouble confirming your registration?{' '}
              <a id="link_support" href="#contact">
                Contact support
              </a>.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ------------------
// Combined Component
// ------------------

const EmailConfirmation: React.FC = () => {

  const appWorkflowState = useSelector((state: RootState) => getAppWorkflowState(state))

  const { setTitle } = useTitleContext()
  
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const [confirmationFailed, setConfirmationFailed] = useState<boolean | null>(null);
  const [callEmailConfirmationAPI] = useEmailConfirmationMutation();

  const hash = window.location.hash;
  const queryString = hash.split('?')[1];
  const urlParams = new URLSearchParams(queryString);
  const formData = {
    Id: urlParams.get('id') || '',
    TimeId: urlParams.get('tid') || ''
  };

  if (appWorkflowState === appWorkflowStates.AUTHENTICATED) {
    return <PageNotAccessibleIfLoggedIn />
  }

  useEffect(() => {

    setTitle("Confirm Email");

    const timer = setTimeout(() => { 
      // Redirecting to URL causes page reload -> component is mounted twice -> API is called twice
      // This timeout PREVENTS the API call from being made twice

    const confirmEmail = async () => {
      try {
        const response: any = await callEmailConfirmationAPI({ ...formData }).unwrap();
        if (response != null) 
        {
          const resultData = response.data;
          if (resultData != null) 
          {
            if (resultData.MessageResults.length > 0)
            {
              for (let i = 0; i < resultData.MessageResults.length; i++) 
              {
                if (resultData.MessageResults[i].HelpMessage == FoundationsEnums.Status.Success) 
                {
                  if (response.statusCode != 400 && resultData.EmailAddress)
                  {
                    setEmailAddress(resultData.EmailAddress);
                    setConfirmationFailed(false);
                    FoundationsGenericToaster('success', resultData.MessageResults[i].Message);
                  }
                }
                else 
                {
                  setConfirmationFailed(true);
                  FoundationsGenericToaster('error', resultData.MessageResults[i].Message);
                }
              }
            }
            else 
            {
              setConfirmationFailed(true);
              FoundationsGenericToaster('error', 'Something went wrong');
            }
          }
          else 
          {
            setConfirmationFailed(true);
            FoundationsGenericToaster('error', 'Something went wrong');
          }
        }
        else {
          setConfirmationFailed(true);
          FoundationsGenericToaster('error', 'Something went wrong');
        }
      } 
      catch (error) 
      {
        console.error('Email confirmation failed:', error);
        setConfirmationFailed(true);
        FoundationsGenericToaster('error', 'Something went wrong');
      }
    };

    if (formData.Id && formData.TimeId) 
    {
      confirmEmail();
    } 
    else 
    {
      console.error('Error with API parameters:', formData);
      setConfirmationFailed(true);
      FoundationsGenericToaster(
        'error',
        `Something went wrong`,
    )
    }

  }, 100); // Slight debounce to counter component being mounted twice in quick succession
  return () => clearTimeout(timer);

  }, []);

  if (confirmationFailed === null) {
    return <div></div>;
  }

  return confirmationFailed ? (
    <EmailConfirmationFailed />
  ) : (
    <EmailConfirmationSuccess emailAddress={emailAddress || ''} />
  )

};

export default EmailConfirmation;
